
import React from 'react';
import { 
  Card, 
  CardContent, 
  CardHeader, 
  CardTitle 
} from '@/components/ui/card';
import { 
  CheckCircle, 
  AlertTriangle, 
  XCircle, 
  CircleDashed,
  Zap
} from 'lucide-react';

interface VoltageRegulatorSummaryStatsProps {
  regulators: any[];
  isLoading: boolean;
}

const VoltageRegulatorSummaryStats: React.FC<VoltageRegulatorSummaryStatsProps> = ({ 
  regulators,
  isLoading 
}) => {
  // Calculate status counts
  const statusCounts = {
    Good: regulators.filter(reg => reg.status === 'Good').length,
    Warning: regulators.filter(reg => reg.status === 'Warning').length,
    Critical: regulators.filter(reg => reg.status === 'Critical').length,
    Offline: regulators.filter(reg => reg.status === 'Offline').length,
  };

  const totalRegulators = regulators.length;
  
  // Calculate average regulation accuracy
  const averageAccuracy = regulators.length > 0 
    ? (regulators.reduce((sum, reg) => sum + reg.readings.regulationAccuracy, 0) / regulators.length).toFixed(1)
    : 0;

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Zap className="h-5 w-5 text-amber-500" />
            <span>Voltage Regulator Health</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center h-40">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <Zap className="h-5 w-5 text-amber-500" />
          <span>Voltage Regulator Health</span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="flex flex-col items-center justify-center p-4 bg-green-50 dark:bg-green-900/10 rounded-md">
            <CheckCircle className="h-8 w-8 text-green-500 mb-2" />
            <span className="text-sm text-muted-foreground">Good</span>
            <span className="text-2xl font-semibold">{statusCounts.Good}</span>
          </div>
          
          <div className="flex flex-col items-center justify-center p-4 bg-amber-50 dark:bg-amber-900/10 rounded-md">
            <AlertTriangle className="h-8 w-8 text-amber-500 mb-2" />
            <span className="text-sm text-muted-foreground">Warning</span>
            <span className="text-2xl font-semibold">{statusCounts.Warning}</span>
          </div>
          
          <div className="flex flex-col items-center justify-center p-4 bg-red-50 dark:bg-red-900/10 rounded-md">
            <XCircle className="h-8 w-8 text-red-500 mb-2" />
            <span className="text-sm text-muted-foreground">Critical</span>
            <span className="text-2xl font-semibold">{statusCounts.Critical}</span>
          </div>
          
          <div className="flex flex-col items-center justify-center p-4 bg-gray-50 dark:bg-gray-800 rounded-md">
            <CircleDashed className="h-8 w-8 text-gray-500 mb-2" />
            <span className="text-sm text-muted-foreground">Offline</span>
            <span className="text-2xl font-semibold">{statusCounts.Offline}</span>
          </div>
        </div>
        
        <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="p-4 border rounded-md">
            <div className="text-sm text-muted-foreground mb-1">Total Regulators</div>
            <div className="text-2xl font-semibold">{totalRegulators}</div>
          </div>
          
          <div className="p-4 border rounded-md">
            <div className="text-sm text-muted-foreground mb-1">Average Accuracy</div>
            <div className="text-2xl font-semibold">{averageAccuracy}%</div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default VoltageRegulatorSummaryStats;
